import { PenTool, Send, Trash2, Upload, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import {
  CLIENT_DOCUMENT_ACCEPT,
  type ChecklistEntry,
  type ForeignNationalDocument
} from "@/api/types/foreignNationalDocument";
import { BrandLoader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { ListSearch } from "@/components/ui/list-toolbar";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";

import { useFocusScroll } from "../../../clientProfile/hooks/useFocusScroll";
import { documentFocusDomId } from "../../documentsModel";
import { useDocumentsChecklist } from "../../hooks/useDocumentsChecklist";
import { useForeignNationalDocuments } from "../../hooks/useForeignNationalDocuments";
import type { CaseFilePreviewSource } from "../DocumentQuickPreview";
import { ForeignNationalDocumentQuickPreviewDrawer } from "../ForeignNationalDocumentQuickPreviewDrawer";
import { ForeignNationalDocumentsSendToCaseDrawer } from "../ForeignNationalDocumentsSendToCaseDrawer";
import { runUploadBatch } from "../UploadProgressToast";
import {
  ChecklistInternalSection,
  ChecklistOtherSection,
  ChecklistSection
} from "./ChecklistSection";
import type { ChecklistRowContext } from "./ChecklistSlotRow";
import {
  attentionCount,
  filterInternal,
  filterOther,
  filterSections,
  isPdfEntry,
  parseDay,
  selectableEntries
} from "./checklistModel";

type Props = {
  foreignNationalId: string;
  clientFirstName?: string | null;
  /** `?documentId=` deep link: scroll to and highlight this document's row. */
  focusDocumentId?: string | null;
  /** Start a request; "documents" opens the documents template. */
  onCreateRequest: (opts?: { templateHint?: string }) => void;
  onRequestSignature?: (doc: ForeignNationalDocument) => void;
};

/** A stand-in record for the preview when the library list hasn't caught up yet. */
const asDocument = (
  foreignNationalId: string,
  entry: ChecklistEntry
): ForeignNationalDocument => ({
  foreign_national_document_id: entry.document_id ?? entry.id,
  foreign_national_id: foreignNationalId,
  filename: entry.filename ?? entry.title ?? "document",
  content_type: entry.mime_type ?? "application/pdf",
  size_bytes: entry.size_bytes ?? 0,
  storage_path: "",
  download_url: entry.download_url ?? "",
  filing_status: entry.filing_status ?? "reviewed",
  label: entry.label ?? null,
  shared_with_client: entry.shared_with_client
});

const errorText = (e: unknown) => (e instanceof Error ? e.message : undefined);

/**
 * The firm's view of a client's documents: one row per type of document,
 * grouped by kind, with what's missing or requested. Client documents and
 * case files are merged; nothing is copied until a row is shared, sent or
 * signed.
 */
export const DocumentsChecklist: FC<Props> = ({
  foreignNationalId,
  clientFirstName = null,
  focusDocumentId = null,
  onCreateRequest,
  onRequestSignature
}) => {
  const navigate = useNavigate();
  const checklist = useDocumentsChecklist(foreignNationalId);
  const library = useForeignNationalDocuments(foreignNationalId);
  const { data, isLoading, errorMessage, refresh } = checklist;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [attention, setAttention] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{
    doc: ForeignNationalDocument;
    caseFile: CaseFilePreviewSource | null;
    casePath: string | null;
  } | null>(null);
  const [sendDocs, setSendDocs] = useState<ForeignNationalDocument[] | null>(
    null
  );

  const today = useMemo(
    () => (data?.today ? parseDay(data.today) : new Date()),
    [data?.today]
  );
  const clientName = clientFirstName || data?.client_name || null;
  const selectable = useMemo(
    () => (data ? selectableEntries(data) : []),
    [data]
  );
  const selectedEntries = useMemo(
    () => selectable.filter((e) => selected.has(e.id)),
    [selectable, selected]
  );

  useEffect(() => {
    const ids = new Set(selectable.map((e) => e.id));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectable]);

  const { highlightedId } = useFocusScroll(
    focusDocumentId ? documentFocusDomId(focusDocumentId) : null,
    Boolean(data)
  );
  const highlightedDocumentId =
    focusDocumentId && highlightedId === documentFocusDomId(focusDocumentId)
      ? focusDocumentId
      : null;

  const libraryDoc = useCallback(
    (id: string) =>
      library.documents.find((d) => d.foreign_national_document_id === id) ??
      null,
    [library.documents]
  );

  /** The client document for an entry; a case-only file is saved to the client first. */
  const toDocument = useCallback(
    async (entry: ChecklistEntry): Promise<ForeignNationalDocument> => {
      if (entry.document_id)
        return (
          libraryDoc(entry.document_id) ?? asDocument(foreignNationalId, entry)
        );
      if (!entry.node)
        throw new Error("This file can't be saved to the client.");
      const saved = await ForeignNationalDocumentsAPI.saveCaseDocument(
        foreignNationalId,
        entry.node.individual_id,
        entry.node.node_id
      );
      library.refresh({ silent: true }).catch(() => undefined);
      return saved;
    },
    [foreignNationalId, library, libraryDoc]
  );

  const refreshAll = useCallback(() => {
    refresh({ silent: true }).catch(() => undefined);
    library.refresh({ silent: true }).catch(() => undefined);
  }, [library, refresh]);

  // The library's copy once it has one, so a save (which refetches the
  // library) shows in the open preview.
  const previewDocument = preview
    ? preview.caseFile
      ? preview.doc
      : libraryDoc(preview.doc.foreign_national_document_id) ?? preview.doc
    : null;

  const toggleSelect = useCallback((entry: ChecklistEntry) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(entry.id)) next.delete(entry.id);
      else next.add(entry.id);
      return next;
    });
  }, []);

  const openSendToCase = useCallback(
    async (entries: ChecklistEntry[]) => {
      try {
        setBusy(true);
        const docs = await Promise.all(entries.map(toDocument));
        setSendDocs(docs);
      } catch (e) {
        toast.error("Couldn't prepare the documents.", {
          description: errorText(e)
        });
      } finally {
        setBusy(false);
      }
    },
    [toDocument]
  );

  const askToSign = useCallback(
    async (entry: ChecklistEntry) => {
      if (!onRequestSignature) return;
      try {
        onRequestSignature(await toDocument(entry));
      } catch (e) {
        toast.error("Couldn't prepare the document.", {
          description: errorText(e)
        });
      }
    },
    [onRequestSignature, toDocument]
  );

  const deleteEntries = useCallback(
    async (entries: ChecklistEntry[]) => {
      const ids = entries
        .map((e) => e.document_id)
        .filter((id): id is string => Boolean(id));
      if (ids.length === 0) {
        toast.error("Case files are deleted from their case.");
        return;
      }
      setBusy(true);
      const results = await Promise.allSettled(
        ids.map((id) => library.remove(id))
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      setBusy(false);
      setSelected(new Set());
      refresh({ silent: true }).catch(() => undefined);
      const skipped = entries.length - ids.length;
      if (failed)
        toast.error("Some deletes failed", {
          description: `${ids.length - failed} deleted, ${failed} failed`
        });
      else
        toast.success(
          ids.length === 1
            ? "Document deleted"
            : `${ids.length} documents deleted`,
          {
            description: skipped
              ? `${skipped} case ${
                  skipped === 1 ? "file stays" : "files stay"
                } in its case.`
              : undefined
          }
        );
    },
    [library, refresh]
  );

  const ctx: ChecklistRowContext = useMemo(
    () => ({
      today,
      clientName,
      selecting: selected.size > 0,
      isSelected: (e) => selected.has(e.id),
      onToggleSelect: toggleSelect,
      highlightedId: highlightedDocumentId,
      onOpen: (entry) => {
        if (entry.document_id) {
          setPreview({
            doc:
              libraryDoc(entry.document_id) ??
              asDocument(foreignNationalId, entry),
            caseFile: null,
            casePath: null
          });
        } else if (entry.node) {
          setPreview({
            doc: asDocument(foreignNationalId, entry),
            caseFile: {
              individualId: entry.node.individual_id,
              nodeId: entry.node.node_id
            },
            casePath: entry.node.path ?? null
          });
        }
      },
      onDownload: (entry) => {
        if (entry.download_url)
          window.open(entry.download_url, "_blank", "noopener");
      },
      onSendToCase: (entry) => void openSendToCase([entry]),
      onAskToSign: onRequestSignature
        ? (entry) => void askToSign(entry)
        : undefined,
      onSetShared: (entry, shared) => {
        checklist
          .setShared(entry, shared)
          .then(() => {
            library.refresh({ silent: true }).catch(() => undefined);
            toast.success(
              shared
                ? `${
                    entry.filename ?? "The document"
                  } is now visible in the client portal.`
                : `${
                    entry.filename ?? "The document"
                  } is hidden from the client.`
            );
          })
          .catch((e: unknown) =>
            toast.error("Couldn't change sharing.", {
              description: errorText(e)
            })
          );
      },
      onDelete: (entry) => void deleteEntries([entry]),
      onMarkReviewed: (entry) => {
        checklist.markReviewed(entry).catch((e: unknown) =>
          toast.error("Couldn't mark it reviewed.", {
            description: errorText(e)
          })
        );
      },
      onAskForRescan: () => onCreateRequest({ templateHint: "documents" })
    }),
    [
      askToSign,
      checklist,
      clientName,
      deleteEntries,
      foreignNationalId,
      highlightedDocumentId,
      library,
      libraryDoc,
      onCreateRequest,
      onRequestSignature,
      openSendToCase,
      selected,
      today,
      toggleSelect
    ]
  );

  const filter = { attention, query };
  const sections = data ? filterSections(data, filter) : [];
  const other = data ? filterOther(data, filter) : [];
  const internal = data ? filterInternal(data, filter) : [];
  const anySelected = selected.size > 0;
  const signable =
    onRequestSignature &&
    selectedEntries.length === 1 &&
    !selectedEntries[0].client_upload &&
    isPdfEntry(selectedEntries[0])
      ? selectedEntries[0]
      : null;
  const name = clientName || "Client";

  const onFilesPicked = (files: File[]) => {
    if (files.length === 0) return;
    runUploadBatch(files, library.upload)
      .then(() => refresh({ silent: true }))
      .catch(() => undefined);
  };

  const header = anySelected ? (
    <>
      <Checkbox
        checked
        aria-label="Clear Selection"
        onCheckedChange={() => setSelected(new Set())}
      />
      <span
        className="text-base font-medium text-fg tabular-nums"
        data-testid="checklist-selected-count"
      >
        {selected.size} Selected
      </span>
      <span className="flex-1" />
      {signable ? (
        <Button disabled={busy} onClick={() => void askToSign(signable)}>
          <PenTool />
          Ask {name} to Sign
        </Button>
      ) : null}
      <Button
        variant="danger"
        disabled={busy}
        onClick={() => void deleteEntries(selectedEntries)}
      >
        {busy ? <Spinner className="size-3.5" /> : <Trash2 />}
        Delete
      </Button>
      <Button
        variant="primary"
        disabled={busy}
        onClick={() => void openSendToCase(selectedEntries)}
      >
        <Send />
        Send to Case
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Clear Selection"
        onClick={() => setSelected(new Set())}
      >
        <X />
      </Button>
    </>
  ) : (
    <>
      <span className="mr-1 shrink-0 text-base font-medium text-fg">
        Documents
      </span>
      <Tabs
        value={attention ? "attention" : "all"}
        onValueChange={(v) => setAttention(v === "attention")}
      >
        <TabsList aria-label="Filter documents">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="attention" data-testid="checklist-attention-tab">
            Needs Attention
            <span className="text-fg-4 tabular-nums">
              {data ? attentionCount(data) : 0}
            </span>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <span className="flex-1" />
      <ListSearch
        value={query}
        onValueChange={setQuery}
        placeholder="Search documents"
        aria-label="Search documents"
        className="w-48"
      />
      <Button onClick={() => fileInputRef.current?.click()}>
        <Upload />
        Upload
      </Button>
      <Button variant="primary" onClick={() => onCreateRequest()}>
        <Send />
        Request Documents
      </Button>
    </>
  );

  const body = () => {
    if (!data)
      return (
        <EmptyState
          className="py-12"
          title="Couldn't Load Documents"
          description={errorMessage ?? undefined}
          action={
            <Button onClick={() => refresh().catch(() => undefined)}>
              Try Again
            </Button>
          }
        />
      );
    if (sections.length === 0 && other.length === 0 && internal.length === 0)
      return (
        <EmptyState
          className="py-12"
          title={
            attention
              ? "Nothing Needs Attention"
              : query.trim()
              ? "No Documents Match"
              : "No Documents Yet"
          }
          description={
            attention
              ? "Nothing new, flagged or expiring."
              : query.trim()
              ? "Try a different search."
              : `Upload a file or request documents from ${name}.`
          }
        />
      );
    return (
      <>
        {sections.map((section) => (
          <ChecklistSection key={section.kind} section={section} ctx={ctx} />
        ))}
        {other.length > 0 ? (
          <ChecklistOtherSection entries={other} ctx={ctx} />
        ) : null}
        {internal.length > 0 ? (
          <ChecklistInternalSection
            cases={internal}
            forceOpen={query.trim().length > 0}
            onOpen={(item) => navigate(item.path)}
          />
        ) : null}
      </>
    );
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-surface"
        data-testid="documents-checklist"
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept={CLIENT_DOCUMENT_ACCEPT}
          data-testid="checklist-file-input"
          onChange={(e) => {
            onFilesPicked(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
        <div
          className="flex h-10 shrink-0 items-center gap-2 border-b border-line-1 pr-2 pl-4"
          data-testid="checklist-header"
        >
          {header}
        </div>
        {!data && isLoading ? (
          <div
            className="flex min-h-0 flex-1 items-center justify-center"
            data-testid="checklist-loading"
          >
            <BrandLoader label="Loading documents" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto pb-4">{body()}</div>
        )}
      </div>

      <ForeignNationalDocumentQuickPreviewDrawer
        open={Boolean(preview)}
        foreignNationalId={foreignNationalId}
        document={previewDocument}
        caseFile={preview?.caseFile ?? null}
        editable
        onDocumentChange={refreshAll}
        onOpenInCase={
          preview?.casePath
            ? () => {
                const path = preview.casePath;
                setPreview(null);
                if (path) navigate(path);
              }
            : undefined
        }
        onOpenChange={(next) => {
          if (!next) setPreview(null);
        }}
      />

      <ForeignNationalDocumentsSendToCaseDrawer
        open={Boolean(sendDocs)}
        foreignNationalId={foreignNationalId}
        documents={sendDocs ?? []}
        onOpenChange={(open) => {
          if (!open) setSendDocs(null);
        }}
        onSuccess={() => {
          setSelected(new Set());
          setSendDocs(null);
          refreshAll();
        }}
      />
    </TooltipProvider>
  );
};
